/**
 * @description Show and hides content areas depending on desired domain
 * search mode.
 * @param {string} mode  - The domain search mode
 * @param {HTMLElement} contentContainer - Container element that houses the content areas
 */
const toggleContentAreas = (
  mode: string,
  contentContainer: HTMLElement
): void => {
  // Init
  const outgoingContentAreas =
    contentContainer.querySelectorAll<HTMLDivElement>(
      `.js-domain-search__content-area:not([data-mode=${mode}])`
    )
  const incomingContentArea = contentContainer.querySelector<HTMLDivElement>(
    `.js-domain-search__content-area:is([data-mode=${mode}])`
  )

  // Check For Null Errors
  if (!outgoingContentAreas) {
    throw new Error('setupDomainSearch: outgoingContentAreas is null')
  }
  if (!incomingContentArea) {
    throw new Error('setupDomainSearch: incomingContentArea is null')
  }

  // Hide Outgoing and Show Incoming Content
  outgoingContentAreas.forEach(
    (contentArea) => (contentArea.style.display = 'none')
  )
  incomingContentArea.style.display = 'block'
}

/**
 * @description Updates the query params string, this makes sure that the user's
 * domain search mode persists even if the page reloads
 * @param {string} mode The domain search mode
 */
const updateQueryParams = (mode: string): void => {
  // Init
  const searchParams = new URLSearchParams(window.location.search)

  // https://stackoverflow.com/a/53929685
  // Manipulate the query params
  if (mode === 'bulk') {
    searchParams.set('mode', mode)
  } else {
    searchParams.delete('mode')
  }

  const updateURL =
    window.location.protocol +
    '//' +
    window.location.host +
    window.location.pathname +
    '?' +
    searchParams.toString()

  window.history.pushState({ path: updateURL }, '', updateURL)
}

/**
 * @description Marks option as ACTIVE i.e. is selected
 * @param {HTMLElement} modeOption - option element to mark as active
 */
const activateOption = (modeOption: HTMLElement): void => {
  modeOption.classList.remove('btn-default')
  modeOption.classList.add('btn-primary')
}

/**
 * @description Marks option as INACTIVE i.e. is NOT selected
 * @param {HTMLElement} modeOption - option element to mark as inactive
 */
const deactivateOption = (modeOption: HTMLElement): void => {
  modeOption.classList.add('btn-default')
  modeOption.classList.remove('btn-primary')
}

/**
 * @description Hooks up the domain-search mode selectors to click events
 * so that the correct content area is selected. In effect, allows users to
 * toggle between simple and bulk domain search
 * @param {NodeListOf<HTMLLabelElement>} modeOptions - a node list of
 * label elements that wrap a radio input
 * @param {HTMLElement} contentContainerEl - contains the content areas
 */
const setupSelectionEventsOnModeOption = (
  modeOptions: NodeListOf<HTMLButtonElement>,
  contentContainerEl: HTMLElement
): void => {
  modeOptions.forEach((modeOption) => {
    modeOption.addEventListener('click', function () {
      // Init Base Vars
      const { mode } = this.dataset
      const sibling = this.previousElementSibling ?? this.nextElementSibling

      // Check For Null Error
      if (!mode || mode === '') {
        throw new Error(
          'setupDomainSearch: mode value is null or an empty string'
        )
      }
      if (!sibling) {
        throw new Error('setupDomainSearch: Sibling is null')
      }

      deactivateOption(sibling as HTMLElement)
      activateOption(this)

      toggleContentAreas(mode, contentContainerEl)

      // Update Query Params String
      updateQueryParams(mode)
    })
  })
}

/**
 * @description Gets and returns the domain search mode from the query params.
 * If the mode is not set or is invalid, it returns null
 * @returns {(String|null)}
 */
const getModeFromQueryParams = (): string | null => {
  // Init
  const ALLOWED_MODES = ['simple', 'bulk']
  // https://stackoverflow.com/a/901144
  const urlSearchParams = new URLSearchParams(window.location.search)

  // Get Mode
  const mode = urlSearchParams.get('mode')

  // check if null or invalid
  if (!mode || !ALLOWED_MODES.includes(mode.toLowerCase())) {
    return null
  }

  return mode
}

/**
 * @description Dispatches click event so that the correct content area
 * is displayed
 * @param {HTMLButtonElement} modeOption - button element
 */
const preSelectModeOption = (modeOption: HTMLButtonElement): void => {
  setTimeout(() => {
    modeOption.dispatchEvent(
      new Event('click', {
        bubbles: true,
        cancelable: true
      })
    )
  }, 0.00001)
}

/**
 * @description Choose the Domain Search Mode to preselect depending on
 * whether or not the query parameter is set e.g. &mode=simple. The default
 * mode is simple domain search
 * @param modeSelectorEl - Container element that houses the mode options
 */
const handlePreSelection = (modeSelectorEl: HTMLElement): void => {
  const preSelectedMode = getModeFromQueryParams()

  let preselectedModeOption: HTMLButtonElement | null = null
  if (preSelectedMode) {
    preselectedModeOption = modeSelectorEl.querySelector<HTMLButtonElement>(
      `.js-domain-search__mode:is([data-mode=${preSelectedMode}])`
    )
  } else {
    preselectedModeOption = modeSelectorEl.querySelector<HTMLButtonElement>(
      '.js-domain-search__simple'
    )
  }

  if (!preselectedModeOption) {
    throw new Error('setupDomainSearch: Preselected Option is null')
  }

  preSelectModeOption(preselectedModeOption)
}

/**
 * @description Bootstraps the mode selection domain search page
 */
export const setupDomainSearchModeSelection = (): void => {
  // Init
  const modeSelectorEl = document.querySelector<HTMLDivElement>(
    '.js-domain-search__mode-options'
  )
  const modeContentEl = document.querySelector<HTMLDivElement>(
    '.js-domain-search__mode-content-container'
  )

  // Exit if Null
  if (!modeSelectorEl || !modeContentEl) {
    return
  }

  // Select Available Options
  const modeOptions = modeSelectorEl.querySelectorAll<HTMLButtonElement>(
    '.js-domain-search__mode'
  )

  // Handle Null Errors
  if (!modeOptions) {
    throw new Error('setupDomainSearch: Mode Options is null')
  }

  setupSelectionEventsOnModeOption(modeOptions, modeContentEl)
  handlePreSelection(modeSelectorEl)
}
